/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { ButtonIcon, Divider, Dropdown, NumberInput, Typography } from "./..";
import OptionWrapper from "../others/option-wrapper";
import { getGlobal } from "../../global";
import PropTypes from 'prop-types';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { bgColor, borderTop, boxBorder, cursorPointer, displayFlex, flexRow, itemsCenter, justifyCenter, mg, mgb, mgl, noPadding, parseHeight, parseMinHeight, parseMinWidth, parseMinWidthHeight, parseWidthHeight, pd, textCenter, textColor, textLeft, whiteSpaceNoWrap } from "../../styles/general";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";
const {
  colors: {
    system: {
      active: systemActive
    }
  },
  typography: {
    paragraph2
  }
} = useTheme();
const getLastPage = (totalItems, itemsPerPage) => {
  let _lastPage = 0;
  if (totalItems && totalItems >= 0 && itemsPerPage && itemsPerPage > 0) {
    _lastPage = Math.ceil(totalItems / itemsPerPage);
    return _lastPage;
  }
  return _lastPage + 1;
};
const delayOnInput = getGlobal('delayOnInput');
const PagingInfo = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  bgColor,
  className,
  currentPage,
  hideWithPage,
  id,
  itemsPerPage,
  listPerPage,
  onChangePage,
  onChangePerPage,
  onChangingPage,
  onChangingPerPage,
  style,
  totalItems,
  typeShort
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const timer = useRef(null);
  const currentPageRef = useRef(currentPage);
  const numberRef = useRef(null);
  const totalRef = useRef(null);
  const [totalItemsState, setTotalItemsState] = useState(totalItems);
  const [itemsPerPageState, setItemsPerPageState] = useState(itemsPerPage || listPerPage[0] || 10);
  const [currentPageState, setCurrentPageState] = useState(currentPage);
  const [listPerPageState, setListPerPageState] = useState(listPerPage);
  const [disablePrevState, setDisablePrevState] = useState(true);
  const [disableNextState, setDisableNextState] = useState(!(totalItems > itemsPerPage));
  const [inputPageValue, setInputPageValue] = useState(currentPage);
  const _PagingInfoCSS = PagingInfoCSS(bgColor, typeShort);
  const _onChangePage = async ({
    page,
    e,
    changeBy
  }) => {
    const lastPage = getLastPage(totalItemsState, itemsPerPageState);
    let disablePrev = false;
    let disableNext = false;

    // If last page = 1. Disable all
    if (lastPage === 1) {
      disablePrev = true;
      disableNext = true;
    }

    // If current page = last page. Disable Next and Last
    if (page + 1 >= lastPage) {
      disableNext = true;
    }

    // If current page = 0. Disable Previous and First
    if (page === 0) {
      disablePrev = true;
    }
    setDisablePrevState(disablePrev);
    setDisableNextState(disableNext);
    if (page === currentPageState) return;
    if (onChangingPage) {
      const event = {
        ...e,
        cancel: false,
        prevPage: currentPageState,
        newPage: page,
        changeBy: changeBy
      };
      await onChangingPage(event);
      if (event.cancel) {
        if (e !== null && e !== void 0 && e.reset) e.reset();
        currentPageRef.current = currentPageState;
        setCurrentPageState(currentPageState);
        setInputPageValue(currentPageState);
        return;
      }
    }
    currentPageRef.current = page;
    setCurrentPageState(page);
    setInputPageValue(page);
    if (onChangePage) onChangePage(page);
  };
  const _onChangePerPage = async per => {
    if (per === itemsPerPageState) return;
    const lastPage = getLastPage(totalItemsState, per);
    per = parseInt(per, 10);
    let disableNext = false;

    // If last page = 1. Disable all
    if (lastPage === 1) {
      disableNext = true;
    }
    if (onChangingPerPage) {
      const event = {
        cancel: false,
        prevPerPage: itemsPerPageState,
        newPerPage: per
      };
      await onChangingPerPage(event);
      if (event.cancel) {
        return;
      }
    }
    setItemsPerPageState(per);
    _onChangePage({
      page: 0
    });
    setDisablePrevState(true);
    setDisableNextState(disableNext);
    if (onChangePerPage) onChangePerPage(per);
  };
  const onTypePaging = e => {
    var _e$value;
    if (timer.current) {
      clearTimeout(timer.current);
    }
    const value = (_e$value = e === null || e === void 0 ? void 0 : e.value) !== null && _e$value !== void 0 ? _e$value : e;
    const lastPage = getLastPage(totalItemsState, itemsPerPageState);
    const page = !value || parseInt(value) < 0 ? 1 : parseInt(value) > lastPage ? lastPage : parseInt(value);
    currentPage = page - 1;
    timer.current = setTimeout(() => {
      _onChangePage({
        page: page - 1,
        e: e
      });
    }, delayOnInput);
  };
  const checkKeyPress = e => {
    if (/,/.test(e.key)) {
      e.preventDefault();
    }
  };
  useEffect(() => {
    setTotalItemsState(totalItems);
  }, [totalItems]);
  useEffect(() => {
    _onChangePerPage(itemsPerPage);
  }, [itemsPerPage]);
  useEffect(() => {
    _onChangePage({
      page: currentPage,
      changeBy: 'currentPage'
    });
  }, [currentPage]);
  useEffect(() => {
    setListPerPageState(listPerPage);
  }, [listPerPage]);
  useEffect(() => {
    _onChangePage({
      page: totalItemsState / itemsPerPageState < currentPageState - 1 ? 0 : currentPageState,
      changeBy: 'totalItems'
    });
  }, [totalItemsState]);
  useEffect(() => {
    _onChangePage({
      page: totalItemsState / itemsPerPageState < currentPageState - 1 ? 0 : currentPageState,
      changeBy: 'itemsPerPage'
    });
  }, [itemsPerPageState]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      onChangePage: page => _onChangePage({
        page: page,
        changeBy: 'ref'
      }),
      onChangePerPage: per => _onChangePerPage(per),
      setTotalItems: items => setTotalItemsState(items),
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    currentRef.onChangePage = page => _onChangePage({
      page: page,
      changeBy: 'ref'
    });
    currentRef.onChangePerPage = per => _onChangePerPage(per);
    currentRef.setTotalItems = items => setTotalItemsState(items);
    return currentRef;
  });
  const updateSize = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      var _ref$current;
      let conti = true;
      [...(((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.children) || [])].reverse().forEach(item => {
        if (item !== null && item !== void 0 && item.style) {
          item.style.display = 'flex';
          if (!conti) {
            item.style.opacity = 1;
            return;
          }
        }
        conti = hiddenEle(item);
      });
    }, 300);
  };
  useLayoutEffect(() => {
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  const hiddenEle = child => {
    setTimeout(() => {
      var _ref$current2;
      const infoChild = child === null || child === void 0 ? void 0 : child.getBoundingClientRect();
      const infoParent = (_ref$current2 = ref.current) === null || _ref$current2 === void 0 ? void 0 : _ref$current2.getBoundingClientRect();
      if (!infoChild || !infoParent) return;

      // getBoundingClientRect includes margin => phải trừ đi margin
      const style = window.getComputedStyle(child) || child.currentStyle;
      const margin = parseInt((style === null || style === void 0 ? void 0 : style.marginLeft) || 0) + parseInt((style === null || style === void 0 ? void 0 : style.marginRight) || 0);
      if (infoChild.right - margin >= infoParent.right) {
        child.style.opacity = 0;
        child.style.setProperty('display', 'none', 'important');
      } else {
        child.style.opacity = 1;
      }
      return infoChild.right >= infoParent.right;
    });
  };
  const setWithNumber = useCallback((totalRefs = totalRef, numberRefs = numberRef) => {
    var _totalRefs;
    if (!totalRefs) totalRefs = totalRef.current;
    if (!numberRefs) numberRefs = numberRef.current;
    if (!totalRefs || !numberRefs) return;
    const infoText = (_totalRefs = totalRefs) === null || _totalRefs === void 0 ? void 0 : _totalRefs.getBoundingClientRect();
    if (infoText && numberRefs) {
      numberRefs.style.width = `${infoText.width * 2 + 6}px`;
      numberRefs.style.minWidth = `${infoText.width * 2 + 6}px`;
    }
  }, [totalRef, numberRef]);
  const checkShowPaging = () => {
    switch (hideWithPage) {
      case 'empty':
        return totalItemsState !== 0;
      case 'single':
        return totalItemsState / itemsPerPageState > 1;
      case 'none':
      default:
        return true;
    }
  };
  return useMemo(() => {
    const lastPage = getLastPage(totalItemsState, itemsPerPageState);
    let listPerPageData = !listPerPageState.includes(itemsPerPageState) ? [{
      name: itemsPerPageState,
      id: itemsPerPageState
    }] : [];
    listPerPageState.map(item => listPerPageData = [...listPerPageData, {
      name: item,
      id: item
    }]);
    listPerPageData = [...new Map(listPerPageData.map(v => [JSON.stringify(v), v])).values()];
    listPerPageData.sort((a, b) => a.id - b.id);
    const showPaging = checkShowPaging();
    return showPaging ? jsx("div", {
      ref: ref,
      css: _PagingInfoCSS,
      id: id,
      style: style,
      className: classNames('DGN-UI-PagingInfo', className)
    }, jsx(ButtonIcon, {
      circular: true,
      viewType: 'ghost',
      size: 'small',
      name: 'ArrowDoubleLeft',
      disabled: disablePrevState,
      onClick: e => {
        setInputPageValue(0);
        currentPageRef.current = 0;
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
          _onChangePage({
            page: 0,
            e: e
          });
        }, delayOnInput);
      }
    }), jsx(ButtonIcon, {
      circular: true,
      viewType: 'ghost',
      size: 'small',
      name: 'ArrowLeft',
      disabled: disablePrevState,
      onClick: e => {
        setInputPageValue(inputPageValue - 1);
        currentPageRef.current = currentPageRef.current === 0 ? 0 : currentPageRef.current - 1;
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
          _onChangePage({
            page: currentPageRef.current,
            e: e
          });
        }, delayOnInput);
      }
    }), jsx("div", {
      css: ViewNumberInput,
      ref: refs => {
        numberRef.current = refs;
        setWithNumber(null, refs);
      }
    }, jsx(NumberInput, {
      disabledNegative: true,
      className: 'inputPage',
      value: inputPageValue ? String(inputPageValue + 1) : '1',
      min: 1,
      autoWidth: true,
      inputStyle: {
        minWidth: 10
      },
      width: 'auto',
      placeholder: '',
      max: lastPage,
      onKeyDown: checkKeyPress,
      onChange: onTypePaging
    }), "/", jsx(Typography, {
      ref: refs => {
        totalRef.current = refs;
        setWithNumber(refs, null);
      },
      css: whiteSpaceNoWrap,
      type: 'p2'
    }, lastPage)), jsx(ButtonIcon, {
      circular: true,
      viewType: 'ghost',
      size: 'small',
      name: 'ArrowRight',
      disabled: disableNextState,
      onClick: e => {
        setInputPageValue(inputPageValue + 1);
        currentPageRef.current = currentPageRef.current === lastPage ? lastPage : currentPageRef.current + 1;
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
          _onChangePage({
            page: currentPageRef.current,
            e: e
          });
        }, delayOnInput);
      }
    }), jsx(ButtonIcon, {
      circular: true,
      viewType: 'ghost',
      size: 'small',
      name: 'ArrowDoubleRight',
      disabled: disableNextState,
      onClick: e => {
        setInputPageValue(lastPage - 1);
        currentPageRef.current = lastPage - 1;
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
          _onChangePage({
            page: lastPage - 1,
            e: e
          });
        }, delayOnInput);
      }
    }), jsx(Typography, {
      type: 'p2',
      ref: hiddenEle,
      className: "txt-line-number"
    }, typeShort ? jsx(Divider, {
      direction: 'vertical',
      height: 24,
      color: 'line/category'
    }) : getGlobal('lineNumber'), jsx(Dropdown, {
      allowSearch: false,
      viewType: 'underlined',
      dataSource: listPerPageData,
      value: itemsPerPageState,
      onChange: e => _onChangePerPage(e === null || e === void 0 ? void 0 : e.value),
      inputStyle: {
        width: `${Math.max(...listPerPageState).toString().length}ch`
      },
      inputProps: {
        readOnly: true
      },
      dropdownItemStyle: {
        color: systemActive
      }
    })), jsx(Typography, {
      type: 'p2',
      ref: hiddenEle,
      className: 'total-items'
    }, typeShort ? jsx(Divider, {
      direction: 'vertical',
      height: 24,
      color: 'line/category'
    }) : getGlobal('total') + ':', totalItemsState)) : null;
  }, [bgColor, className, hideWithPage, style, typeShort, currentPageState, disableNextState, disablePrevState, inputPageValue, itemsPerPageState, listPerPageState, totalItemsState]);
}));
const ViewNumberInput = css`
    ${displayFlex};
    ${flexRow};
    ${itemsCenter};
    ${justifyCenter};
    ${mg([0, 1])};
    transition: all 0.1s;
`;
const PagingInfoCSS = (bgCl, typeShort) => css`
    ${displayFlex};
    ${flexRow};
    ${itemsCenter};
    ${paragraph2};
    ${boxBorder};
    ${parseHeight(40)};
    ${borderTop(1, 'line/system')};
    ${pd([1, 0])};
    ${bgColor(bgCl)};
    ${textColor('text/main')};
    .inputPage {
        ${textCenter};
        ${mg([0, 0.5])};
        .DGN-UI-InputBase {
            ${noPadding};
            input {
                ${paragraph2};
                ${textCenter};
                ${textColor('system/active')};
                ${textLeft};
            }
        }
    }
    .txt-line-number {
        ${displayFlex};
        ${whiteSpaceNoWrap};
        ${itemsCenter};
        opacity: 0;
        transition: opacity 0.3s ease;
    }
    .DGN-UI-Divider {
        ${mg([0, 2])};
    }
    .DGN-UI-Dropdown {
        ${mgl([typeShort ? 0 : 2])};
        ${mgb(0)};
        ${parseMinWidth(52)};
        .DGN-UI-Dropdown-Form {
            ${parseMinHeight(24)};
            ${pd(0)};
            input,
            span {
                ${paragraph2};
                ${noPadding};
                ${cursorPointer};
            }
            .DGN-UI-Dropdown-Icon {
                ${mgl([1.5])};
                div,
                span,
                svg {
                    ${parseWidthHeight(20)};
                    ${parseMinWidthHeight(20)};
                }
            }
        }
    }
    .total-items {
        ${displayFlex};
        ${flexRow};
        ${whiteSpaceNoWrap};
        ${itemsCenter};
        ${mgl([typeShort ? 0 : 4])};
        opacity: 0;
        transition: opacity 0.3s ease;
    }
`;
PagingInfo.defaultProps = {
  bgColor: 'system/white',
  className: '',
  currentPage: 0,
  hideWithPage: 'none',
  listPerPage: [10, 15, 20, 30, 50],
  style: {},
  totalItems: 0,
  typeShort: false
};
PagingInfo.propTypes = {
  /** Background color for component. */
  bgColor: PropTypes.string,
  /** Class for component. */
  className: PropTypes.string,
  /** Specifies the current page. */
  currentPage: PropTypes.number,
  /** Hidden mode for page selector. */
  hideWithPage: PropTypes.oneOf(['none', 'empty', 'single']),
  /** The quantity of items per page. */
  itemsPerPage: PropTypes.number,
  /** The list to choose the number of elements to display per page. */
  listPerPage: PropTypes.array,
  /** Callback fired when page number changed. */
  onChangePage: PropTypes.func,
  /** Callback fired when quantity on per page changed. */
  onChangePerPage: PropTypes.func,
  /**
   * Callback fired when page number is changing.
   *
   * * prevPage: Page before changed
   * * newPage: Page after changed
   * * cancel(value): Function cancel change page
   *  * @param {value} - bool
   */
  onChangingPage: PropTypes.func,
  /**
   * Callback fired when quantity on item per page is changing.
   *
   * * prevPerPage: Items per page before changed
   * * newPerPage: Items per page after changed
   * * cancel(value): Function cancel change items per page
   *  * @param {value} - bool
   */
  onChangingPerPage: PropTypes.func,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Total items. */
  totalItems: PropTypes.number,
  /** Compact type for mobile. */
  typeShort: PropTypes.bool
  /**
   * ref methods (ref.current.instance.*method*)
   *
   * * onChangePage(page): Change page number
   *  * @param {page} - number
   * * onChangePerPage(per): Change quantity on per page
   *  * @param {per} - number
   * * setTotalItems(items): Set total items
   *  * @param {items} - number
   *
   * * option(): Gets all UI component properties
   *  * Returns value - object
   * * option(optionName): Gets the value of a single property
   *  * @param {optionName} - string
   *  * Returns value - any
   * * option(optionName, optionValue): Updates the value of a single property
   *  * @param {optionName} - string
   *  * @param {optionValue} - any
   * * option(options): Updates the values of several properties
   *  * @param {options} - object
   */
};

export { PagingInfo };
export default OptionWrapper(PagingInfo);